import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Fade,
} from "@mui/material";
import {
    SmartToy as SmartToyIcon,
    LocationOn as LocationOnIcon,
    Share as ShareIcon,
    Lightbulb as LightbulbIcon,
    Whatshot as WhatshotIcon,
    PhoneAndroid as PhoneAndroidIcon,
} from "@mui/icons-material";

const features = [
    {
        id: 1,
        icon: <SmartToyIcon sx={{ fontSize: 40 }} />,
        title: "AI Quran Chat & Tafsir",
        description:
            "Ask any Quran question and get clear Islamic guidance, translations, and tafsir-style explanations for beginners and students.",
    },
    {
        id: 2,
        icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
        title: "Salah Prayer Times & Qibla",
        description:
            "Never miss Fajr, Dhuhr, Asr, Maghrib, or Isha — accurate location-based Namaz times with smart reminders and Qibla direction.",
    },
    {
        id: 3,
        icon: <ShareIcon sx={{ fontSize: 40 }} />,
        title: "Daily Ayat & Sharing",
        description:
            "Receive inspiring Holy Quran verses every day. Save, reflect, and share ayat with family and friends.",
    },
    {
        id: 4,
        icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
        title: "Islamic Learning & Dua",
        description:
            "Grow your deen with daily wisdom, dua guidance, and practical Islamic insights for modern Muslim life.",
    },
    {
        id: 5,
        icon: <WhatshotIcon sx={{ fontSize: 40 }} />,
        title: "Habit & Streak Tracking",
        description:
            "Build consistent Quran reading and prayer habits with motivating streaks designed for spiritual growth.",
    },
    {
        id: 6,
        icon: <PhoneAndroidIcon sx={{ fontSize: 40 }} />,
        title: "Offline Quran Access",
        description:
            "Read Quran content and Islamic resources even without internet — perfect for travel, work, or quiet reflection.",
    },
];

const FeaturesSection = () => {
    return (
        <Box
            sx={{
                py: { xs: 8, md: 12, lg: 16 },
                backgroundColor: "background.default",
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                        }}
                    >
                        Everything Muslims Need for Quran & Salah
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 640,
                            mx: "auto",
                            lineHeight: 1.6,
                        }}
                    >
                        From AI Quran chat and tafsir to prayer times, Qibla, daily
                        ayat, and deen habits — one peaceful Islamic companion app.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} lg={4} key={feature.id}>
                            <Fade in timeout={800 + index * 200}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        textAlign: "center",
                                        position: "relative",
                                        overflow: "hidden",
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 4,
                                            background:
                                                "linear-gradient(90deg, #2E8B57 0%, #DAA520 100%)",
                                            opacity: 0,
                                            transition: "opacity 0.3s ease",
                                        },
                                        "&:hover::before": {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 80,
                                                height: 80,
                                                bgcolor: "primary.light",
                                                borderRadius: "50%",
                                                mx: "auto",
                                                mb: 3,
                                                color: "white",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    transform:
                                                        "scale(1.1) rotate(5deg)",
                                                },
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>

                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                mb: 2,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default FeaturesSection;
